import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

/**
 * Register the PWA service worker with aggressive update behavior so a new
 * deployment always supersedes a cached bundle — preventing the "stale JS
 * crash" class of bugs.
 *
 * Strategy:
 *  1. Register /service-worker.js at scope /.
 *  2. Whenever a new SW version is installed but waiting, tell it to
 *     skipWaiting (our SW listens for SKIP_WAITING) and then reload the page
 *     so the new bundle is picked up immediately.
 *  3. Poll for SW updates every 30 minutes while the tab is alive so
 *     long-lived PWAs don't get stuck on an old version.
 */
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    // Proactively force a fresh SW check on every app load so stale workers
    // from older deployments get replaced right away.
    navigator.serviceWorker.getRegistrations?.().then((regs) => {
      regs.forEach((r) => { try { r.update(); } catch (_) {} });
    }).catch(() => {});

    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        const activateWaiting = (w) => {
          try { w.postMessage({ type: "SKIP_WAITING" }); } catch (_) {}
        };
        if (reg.waiting) activateWaiting(reg.waiting);
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              activateWaiting(newWorker);
            }
          });
        });
        // Poll for updates every 30 min while open, AND every time the tab
        // becomes visible — covers riders returning to the home-screen PWA.
        setInterval(() => { reg.update().catch(() => {}); }, 30 * 60 * 1000);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            reg.update().catch(() => {});
          }
        });
      })
      .catch(() => {});

    // When the new SW takes control, reload once so the fresh bundle is used.
    let reloadedForNewSw = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloadedForNewSw) return;
      reloadedForNewSw = true;
      window.location.reload();
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
